import { keepPreviousData, queryOptions, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiClient } from '../client';
import {
  COLLECTION_EVENT_TYPES,
  COLLECTION_STATUSES,
  type CollectionEvent,
  type CollectionRow,
  type CollectionsPage,
  type CollectionStatus,
} from '@/types/collection';

const collectionRowSchema = z.object({
  id: z.string(),
  awb: z.string(),
  customerName: z.string(),
  handlerName: z.string().nullable(),
  pickupAddress: z.string().nullable(),
  unit: z.string(),
  totalUnits: z.number().nullable(),
  preAlertId: z.string(),
  partNumber: z.number(),
  partCount: z.number(),
  awbTotalUnits: z.number(),
  status: z.enum(COLLECTION_STATUSES),
  noaRegisteredAt: z.string().nullable(),
  hasNoaDocument: z.boolean(),
  collectBy: z.string().nullable(),
  isDelayed: z.boolean(),
  customsClearedAt: z.string().nullable(),
  collectedUnits: z.number().nullable(),
  collectedAt: z.string().nullable(),
  collectedBy: z.string().nullable(),
  hasDeviation: z.boolean(),
  eventCount: z.number(),
  wasCorrected: z.boolean(),
  originalCollectedUnits: z.number().nullable(),
}) satisfies z.ZodType<CollectionRow>;

const collectionsPageSchema = z.object({
  items: z.array(collectionRowSchema),
  skip: z.number(),
  take: z.number(),
  total: z.number(),
}) satisfies z.ZodType<CollectionsPage>;

const collectionEventSchema = z.object({
  id: z.string(),
  type: z.enum(COLLECTION_EVENT_TYPES),
  collectedUnits: z.number(),
  hasDeviation: z.boolean(),
  collectedBy: z.string(),
  organisationName: z.string(),
  collectedAt: z.string(),
  reason: z.string().nullable(),
  supersededByEventId: z.string().nullable(),
}) satisfies z.ZodType<CollectionEvent>;

const collectionEventsResponseSchema = z.object({
  items: z.array(collectionEventSchema),
});

export interface CollectionsListParams {
  status: CollectionStatus;
  handlerId?: string | undefined;
  /** Calendar date (`YYYY-MM-DD`) to filter by `customsClearedAt` — a single day, not a range. */
  customsClearedAt?: string | undefined;
  skip?: number | undefined;
  take?: number | undefined;
}

/**
 * Prefix shared by every cached collections list page, whatever its
 * status/handlerId/skip/take params. Match on this to invalidate all of them
 * at once instead of guessing a single fully-specified key.
 */
export const collectionsListQueryKey = ['collections', 'list'] as const;

export function collectionsQueryKey(params: CollectionsListParams) {
  return [...collectionsListQueryKey, params] as const;
}

function buildCollectionsQuery(params: CollectionsListParams) {
  const query = new URLSearchParams();
  query.set('status', params.status);
  if (params.handlerId) query.set('handlerId', params.handlerId);
  if (params.customsClearedAt) query.set('customsClearedAt', params.customsClearedAt);
  if (params.skip !== undefined) query.set('skip', String(params.skip));
  if (params.take !== undefined) query.set('take', String(params.take));
  return `?${query.toString()}`;
}

/**
 * GET /api/v1/driver/collections, skip/take paged (docs/driver-collections-pagination-backend.md).
 * Used for both the `Ready` and `Collected` groups — both are prev/next paged
 * behind `TablePagination` now, the same as every other list in the dashboard.
 */
export function collectionsQueryOptions(api: ApiClient, params: CollectionsListParams) {
  return queryOptions({
    queryKey: collectionsQueryKey(params),
    queryFn: async () =>
      collectionsPageSchema.parse(
        await api.request<unknown>(`/api/v1/driver/collections${buildCollectionsQuery(params)}`),
      ),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function collectionEventsQueryKey(id: string) {
  return ['collections', 'events', id] as const;
}

/** GET /api/v1/driver/collections/{id}/events (spec 4.5). No paging — a chain is a handful of events. */
export function collectionEventsQueryOptions(api: ApiClient, id: string) {
  return queryOptions({
    queryKey: collectionEventsQueryKey(id),
    queryFn: async () =>
      collectionEventsResponseSchema.parse(
        await api.request<unknown>(`/api/v1/driver/collections/${encodeURIComponent(id)}/events`),
      ).items,
    staleTime: 30 * 1000,
  });
}

export interface ConfirmCollectionRequest {
  collectedUnits: number;
}

/**
 * POST /api/v1/driver/collections/{id}/confirm (spec 4.2). `collectedBy`,
 * `collectedAt` and organisation are never sent — the server derives them
 * from the authenticated session, matching `updateUserMutationOptions`'s rule
 * of never trusting client-asserted identity fields.
 */
export function confirmCollectionMutationOptions(api: ApiClient) {
  return {
    mutationFn: async ({ id, body }: { id: string; body: ConfirmCollectionRequest }) =>
      collectionRowSchema.parse(
        await api.request<unknown>(`/api/v1/driver/collections/${encodeURIComponent(id)}/confirm`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      ),
  };
}

export interface CorrectCollectionRequest {
  collectedUnits: number;
  reason: string;
}

/** POST /api/v1/driver/collections/{id}/corrections (spec 4.3) — an append-only correction, not an edit. */
export function correctCollectionMutationOptions(api: ApiClient) {
  return {
    mutationFn: async ({ id, body }: { id: string; body: CorrectCollectionRequest }) =>
      collectionRowSchema.parse(
        await api.request<unknown>(
          `/api/v1/driver/collections/${encodeURIComponent(id)}/corrections`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
      ),
  };
}

/**
 * GET /api/v1/driver/collections/{id}/noa (spec 4.4). Every API call carries a
 * bearer token, so a plain `<a href>` would arrive unauthenticated — this
 * fetches the PDF through the authenticated client instead, for the caller to
 * turn into an object URL and a synthesised download click (spec 4.3 of the
 * UI plan). Falls back to `NOA-{awb}.pdf` when the server sends no filename.
 */
export async function fetchNoaDocument(
  api: ApiClient,
  id: string,
  awb: string,
  partNumber: number,
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.requestRaw(`/api/v1/driver/collections/${encodeURIComponent(id)}/noa`);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob, filename: match?.[1] ?? `NOA-${awb}-part${partNumber}.pdf` };
}

/**
 * Moves a row from the `Ready` cache to the head of the `Collected` cache
 * after a successful confirm, so the screen does not have to refetch both
 * lists. Doing this inline in the component is how the two caches drift
 * apart — see docs/driver-collections-ui-plan.md §4.1.
 *
 * Only prepended onto the `Collected` cache when that cache holds page 1
 * (`skip === 0`) — prepending onto a later page would show the row in the
 * wrong position, and the `collectionsListQueryKey` invalidation the caller
 * also does already covers the rest (docs/driver-collections-pagination-frontend.md §2.1).
 * `total` is deliberately left untouched on both caches: the invalidation
 * refetches it, and a hand-decremented count that disagrees with the server
 * is worse than a briefly stale one.
 */
export function moveCollectedRow(
  queryClient: QueryClient,
  keys: { readyKey: readonly unknown[]; collectedKey: readonly unknown[] },
  row: CollectionRow,
) {
  queryClient.setQueryData<CollectionsPage>(keys.readyKey, (old) =>
    old ? { ...old, items: old.items.filter((r) => r.id !== row.id) } : old,
  );
  queryClient.setQueryData<CollectionsPage>(keys.collectedKey, (old) =>
    old && old.skip === 0 ? { ...old, items: [row, ...old.items] } : old,
  );
}

/**
 * Patches a corrected row in place wherever it is cached in the `Collected`
 * list, and invalidates its event history — the chain grew, so a cached
 * history is stale. Invalidated rather than appended by hand: the server is
 * the only thing that knows the new event's id, display name and deviation
 * flag.
 */
export function patchCollectedRow(
  queryClient: QueryClient,
  collectedKey: readonly unknown[],
  row: CollectionRow,
) {
  queryClient.setQueryData<CollectionsPage>(collectedKey, (old) =>
    old ? { ...old, items: old.items.map((r) => (r.id === row.id ? row : r)) } : old,
  );
  void queryClient.invalidateQueries({ queryKey: collectionEventsQueryKey(row.id) });
}
